import { Controller } from "@hotwired/stimulus"

const NO_ROOM = "no_room"

export default class extends Controller {
  static targets = [
    "offerType",
    "availabilityDays",
    "availabilitySeasonalOpening",
    "availabilityWrapper",
    "availabilitySelector",
    "availabilitySeasons",
    "availabilitySeasonalOpeningElement",
    "location",
    "fixedPrice",
    "fullPrice",
    "fixedGroupPrice",
    "reducedFixedGroupPrice",
    "reducedPrice",
    "minParticipants",
    "guideSkills"
  ]

  static values = {
    roomTypes: Array,
    noRoomLabel: String
  }

  connect() {
    this.onActivityAdded = this.onActivityAdded.bind(this)
    this.element.addEventListener("offer:activity-added", this.onActivityAdded)

    this.allSeasonalOpeningOptions =
      this.availabilitySeasonalOpeningElementTarget.cloneNode(true)
    if (this.locationTarget.value) {
      this.filterSeasonalOpeningOptions(this.locationTarget.value, false)
    }
    this.syncAddActivityLink()
    this.categoryChanged()
  }

  disconnect() {
    this.element.removeEventListener("offer:activity-added", this.onActivityAdded)
  }

  onActivityAdded() {
    this.updateRoomTypeSelects(false)
  }

  roomTypeSaved(event) {
    event.target.dataset.savedRoomTypeId = event.target.value
  }

  filterSeasonalOpeningOptions(location, removeSelected = true) {
    const openingSelect = this.availabilitySeasonalOpeningElementTarget
    let newSelect =
      this.availabilitySeasonalOpeningElementTarget.cloneNode(true)
    openingSelect.replaceChildren()

    for (let element of newSelect.children) {
      if (removeSelected && element.selected) {
        element.selected = false
      }
      if (
        !element.value ||
        (element.dataset &&
          element.dataset.location &&
          element.dataset.location == location)
      ) {
        openingSelect.appendChild(element.cloneNode(true))
      }
    }
  }

  locationChanged(event) {
    const locationId = event.target.value
    this.availabilitySeasonalOpeningElementTarget.replaceChildren(
      ...this.allSeasonalOpeningOptions.cloneNode(true).children
    )
    this.filterSeasonalOpeningOptions(locationId)
    this.syncAddActivityLink()
    this.updateRoomTypeSelects()
  }

  syncAddActivityLink() {
    const link = this.element.querySelector("a.add-activity")
    if (!link?.href) return

    const url = new URL(link.href, window.location.origin)
    const locationId = this.locationTarget.value
    if (locationId) {
      url.searchParams.set("location_id", locationId)
    } else {
      url.searchParams.delete("location_id")
    }
    link.href = url.pathname + url.search
  }

  savedRoomTypeId(select) {
    const saved = select.dataset.savedRoomTypeId
    if (saved != null && saved !== "") return String(saved)
    return String(select.value || NO_ROOM)
  }

  updateRoomTypeSelects(resetInvalid = true) {
    const locationId = this.locationTarget.value
    const filtered = this.roomTypesValue.filter((roomType) => {
      if (!locationId) return true
      return roomType.location_ids.map(String).includes(locationId)
    })
    const noRoomLabel = this.noRoomLabelValue

    this.element.querySelectorAll(".type-select").forEach((select) => {
      const saved = this.savedRoomTypeId(select)
      let types = [...filtered]

      if (saved !== NO_ROOM && !types.some((rt) => String(rt.id) === saved)) {
        const entry = this.roomTypesValue.find((rt) => String(rt.id) === saved)
        if (entry) {
          types = [...types, entry].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
          )
        }
      }

      const movieType = types.find((rt) => rt.slug === "movie")

      select.replaceChildren()
      types.forEach((rt) => {
        const option = document.createElement("option")
        option.value = String(rt.id)
        option.textContent = rt.name
        if (rt.slug) option.dataset.slug = rt.slug
        select.appendChild(option)
      })

      const noRoomOption = document.createElement("option")
      noRoomOption.value = NO_ROOM
      noRoomOption.textContent = noRoomLabel
      select.appendChild(noRoomOption)

      if (movieType) {
        select.dataset.movieRoomTypeId = String(movieType.id)
      } else {
        delete select.dataset.movieRoomTypeId
      }

      const validValues = [...types.map((rt) => String(rt.id)), NO_ROOM]
      if (validValues.includes(saved)) {
        select.value = saved
        select.dataset.savedRoomTypeId = saved
      } else if (resetInvalid) {
        select.value = NO_ROOM
        select.dataset.savedRoomTypeId = NO_ROOM
      }
    })

    this.categoryChanged()
  }

  toggleAvailabilities(event) {
    const input = event.target.form.querySelector(
      'input[value="' + event.target.htmlFor + '"]'
    )
    if (input == null) return

    this.availabilityDaysTarget.classList.add("d-none")
    this.availabilitySeasonsTarget.classList.add("d-none")
    this.availabilityWrapperTarget.classList.add("d-none")
    if (input.value == "days") {
      this.availabilityDaysTarget.classList.remove("d-none")
    } else if (input.value == "seasons") {
      this.availabilitySeasonsTarget.classList.remove("d-none")
      window.dispatchEvent(new Event("resize"))
    } else if (input.value == "always") {
      this.availabilityWrapperTarget.classList.remove("d-none")
    }
  }

  toggleAvailabilitySeasonalOpenings(event) {
    if (this.availabilitySelectorTarget.value != "always") {
      this.availabilitySeasonalOpeningTarget.classList.remove("d-none")
    } else {
      this.availabilitySeasonalOpeningTarget.classList.add("d-none")
    }
  }

  toggleFixedPrice(event) {
    if (this.offerTypeTarget.value == "single") {
      this.fixedPriceTarget.classList.toggle("d-none")
      this.fullPriceTarget.classList.toggle("d-none")
      this.reducedPriceTarget.classList.toggle("d-none")
      this.minParticipantsTarget.classList.toggle("d-none")
    } else if (this.offerTypeTarget.value == "group") {
      this.fixedGroupPriceTarget.classList.toggle("d-none")
      this.reducedFixedGroupPriceTarget.classList.toggle("d-none")
      this.minParticipantsTarget.classList.toggle("d-none")
      this.fullPriceTarget.classList.toggle("d-none")
      this.reducedPriceTarget.classList.toggle("d-none")
    }
  }

  toggleNeedsGuide(event) {
    this.guideSkillsTarget.classList.toggle("d-none")
  }

  categoryChanged() {
    const category = this.element.querySelector(".offer-category")?.value
    const activities = this.element.querySelector(".activities")
    if (!category || !activities) return

    if (category == "movie") {
      activities.innerHTML =
        activities.querySelectorAll(".element")[0].outerHTML
    }

    this.element
      .querySelector(".add-activity")
      ?.classList.toggle("d-none", category == "movie")
    this.element
      .querySelectorAll(".remove-activity")
      .forEach((btn) => btn.classList.toggle("d-none", category == "movie"))

    this.element.querySelectorAll(".type-select").forEach((select) => {
      const movieRoomTypeId = select.dataset.movieRoomTypeId
      if (category == "movie" && movieRoomTypeId) {
        select.value = movieRoomTypeId
      }
      select.querySelectorAll("option").forEach((option) => {
        option.toggleAttribute(
          "disabled",
          option.value != movieRoomTypeId && category == "movie"
        )
      })
    })
  }
}
